import type { Order } from '../domain/entities/Order.js';
import type { AgentKPIs, Period, KPI } from '../domain/entities/KPI.js';

export interface Targets {
  revenueTarget: number;
  unitsTarget: number;
  clientCountTarget: number;
}

interface Window {
  fromCurrent: Date;
  toCurrent: Date;
  fromPrev: Date;
  toPrev: Date;
}

export function windowFor(period: Period, now = new Date()): Window {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  if (period === 'month') {
    return {
      fromCurrent: new Date(Date.UTC(y, m, 1)),
      toCurrent: new Date(Date.UTC(y, m + 1, 1)),
      fromPrev: new Date(Date.UTC(y - 1, m, 1)),
      toPrev: new Date(Date.UTC(y - 1, m + 1, 1)),
    };
  }
  if (period === 'quarter') {
    const qStart = Math.floor(m / 3) * 3;
    return {
      fromCurrent: new Date(Date.UTC(y, qStart, 1)),
      toCurrent: new Date(Date.UTC(y, qStart + 3, 1)),
      fromPrev: new Date(Date.UTC(y - 1, qStart, 1)),
      toPrev: new Date(Date.UTC(y - 1, qStart + 3, 1)),
    };
  }
  // ytd
  return {
    fromCurrent: new Date(Date.UTC(y, 0, 1)),
    toCurrent: new Date(Date.UTC(y + 1, 0, 1)),
    fromPrev: new Date(Date.UTC(y - 1, 0, 1)),
    toPrev: new Date(Date.UTC(y, 0, 1)),
  };
}

function within(orders: Order[], from: Date, to: Date): Order[] {
  return orders.filter((o) => {
    const t = new Date(o.date).getTime();
    return t >= from.getTime() && t < to.getTime();
  });
}

function aggregate(orders: Order[]): { revenue: number; units: number; clients: number } {
  let revenue = 0;
  let units = 0;
  const clientIds = new Set<string>();
  for (const o of orders) {
    revenue += o.totalEUR;
    for (const l of o.lines) units += l.quantity;
    clientIds.add(o.clientId);
  }
  return { revenue, units, clients: clientIds.size };
}

function kpi(current: number, previousYear: number, target: number): KPI {
  return { current, previousYear, target };
}

export function computeKPIs(orders: Order[], period: Period, targets: Targets, now = new Date()): AgentKPIs {
  const w = windowFor(period, now);
  const cur = aggregate(within(orders, w.fromCurrent, w.toCurrent));
  const prev = aggregate(within(orders, w.fromPrev, w.toPrev));
  return {
    period,
    revenue: kpi(cur.revenue, prev.revenue, targets.revenueTarget),
    units: kpi(cur.units, prev.units, targets.unitsTarget),
    clientCount: kpi(cur.clients, prev.clients, targets.clientCountTarget),
  };
}

export function pctOfTarget(k: KPI): number {
  return k.target > 0 ? k.current / k.target : 0;
}

export function yoyDelta(k: KPI): number {
  return k.previousYear > 0 ? (k.current - k.previousYear) / k.previousYear : 0;
}
